// Flattens an item-bidding entity's `processedEntries` ledger into bid-history
// rows (newest first) for BidHistoryList. The ledger is append-only: each entry
// wraps a `rawEntry` (what came in) plus the `engineActions` the engine took.
//
// The history surfaces these kinds of row, tagged by `kind`:
//   'bid'       — a `proposeBid` the engine accepted (an updateProcessedEntity)
//   'reject'    — a `proposeBid` the engine turned down (a rejectBid, with reason)
//   'increment' — a `clerkSetBidIncrement` (the clerk changed the ladder step)
//   'asking'    — a `clerkSetAskingPrice` (the clerk moved the asking price while
//                 fishing; carries `rejected`/`reason` when the engine turned it
//                 down for not being in an ASKING state)
//   'proxyDelete' — a `clerkDeleteProxyBids` (the clerk wiped every proxy bid on
//                 the lot; `count` is how many were cleared, `rejected`/`reason`
//                 when the engine turned it down for a finalized item)
//   'reset'     — a `clerkResetBidding` (the clerk wound the asset back to asking;
//                 `rejected`/`reason` when the engine turned it down for a
//                 finalized item)
//   'floorOverride' — a `clerkFloorOverride` (the clerk handed the high bid to
//                 the floor: FLOOR badge, ONLINE bid type)
//   'undo'      — a `clerkUndo` (the clerk stepped the asset back one bidding
//                 action; `bid` is the restored amount, `rejected`/`reason` when
//                 the asset was finalized or there was nothing to undo)
//   'pause'     — a `pauseSale` (the engine was paused, e.g. for finalization;
//                 carries the `reason`)
//   'resume'    — a `resumeSale` (the engine was resumed, e.g. a finalization
//                 was canceled)
//   'finalize'  — a `finalizeSale` (the clerk settled the asset; `finalState`,
//                 `label`, `amount` and `badge` describe the outcome, with
//                 `rejected`/`reason` when the asset was already finalized)
// Everything else in the ledger (engineStarted/endSale/recovery) is lifecycle
// noise and is skipped.

// Channel → SourceBadge letter (F/O/P). In the seed data the per-bid `badge`
// carries the channel (e.g. "FLOOR"); `bidType` ("ONLINE") is the accepting
// action's fallback. Real paddle/bidder identity isn't present yet.
const SOURCE_MAP = { FLOOR: 'floor', ONLINE: 'online', PREBID: 'prebid' };
const SOURCE_LABEL = { floor: 'Floor', online: 'Bid', prebid: 'Prebid' };

// finalState → history label (mirrors ConfirmSalePanel's STATUS_LABELS).
const FINAL_STATE_LABEL = { SOLD: 'Sold', 'NOT-SOLD': 'No Sale', OFFER: 'Offer' };

function sourceFor(rawEntry, acceptAction) {
  const badge = String(rawEntry?.badge ?? '').toUpperCase();
  const bidType = String(acceptAction?.newState?.bidType ?? '').toUpperCase();
  // Only the literal FLOOR badge is a floor bid. A real bidder badge isn't a
  // channel keyword and the first accept often carries no bidType, so fall back
  // to a generic online bid ("Bid") — never 'floor', which reads as a floor bid.
  return SOURCE_MAP[badge] ?? SOURCE_MAP[bidType] ?? 'online';
}

// h:mm:ss to match the history design (no meridiem), from the ISO `timeReceived`.
function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours() % 12 || 12;
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function toRow(entry) {
  const raw = entry.rawEntry;
  const t = formatTime(raw.timeReceived);
  const actions = entry.engineActions ?? [];

  if (raw.rawEntryType === 'clerkSetBidIncrement') {
    const change = actions.find((a) => a.type === 'updateProcessedEntity');
    return {
      id: entry.processedEntryId,
      kind: 'increment',
      increment: raw.increment,
      // previous ladder step, when the engine recorded the transition
      prevIncrement: change?.previousState?.increment ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'clerkSetAskingPrice') {
    // Accepted → updateProcessedEntity (newState.bid); rejected when the item
    // has left the ASKING state → rejectClerkSetAskingPrice, with the reason.
    const reject = actions.find((a) => a.type === 'rejectClerkSetAskingPrice');
    const change = actions.find((a) => a.type === 'updateProcessedEntity');
    return {
      id: entry.processedEntryId,
      kind: 'asking',
      askingPrice: raw.askingPrice,
      // previous asking price, when the engine recorded the transition
      prevAskingPrice: change?.previousState?.bid ?? null,
      rejected: !!reject,
      reason: reject?.rejectReason ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'clerkDeleteProxyBids') {
    // Accepted → updateProcessedEntity clearing proxyBids (previousState holds
    // the wiped list); rejected when the item is finalized →
    // rejectClerkDeleteProxyBids, with the reason.
    const reject = actions.find((a) => a.type === 'rejectClerkDeleteProxyBids');
    const change = actions.find((a) => a.type === 'updateProcessedEntity');
    return {
      id: entry.processedEntryId,
      kind: 'proxyDelete',
      count: change?.previousState?.proxyBids?.length ?? 0,
      rejected: !!reject,
      reason: reject?.reason ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'clerkResetBidding') {
    // Accepted → updateProcessedEntity (status/bid back to asking, badge cleared);
    // rejected when the item is finalized → rejectClerkResetBidding, with reason.
    const reject = actions.find((a) => a.type === 'rejectClerkResetBidding');
    return {
      id: entry.processedEntryId,
      kind: 'reset',
      rejected: !!reject,
      reason: reject?.reason ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'clerkFloorOverride') {
    return {
      id: entry.processedEntryId,
      kind: 'floorOverride',
      t,
    };
  }

  if (raw.rawEntryType === 'clerkUndo') {
    // Accepted → updateProcessedEntity restoring prior state (newState.bid is the
    // rolled-back amount, when the reversal changed the bid); rejected when the
    // lot is finalized or there's nothing left to undo → rejectClerkUndo.
    const reject = actions.find((a) => a.type === 'rejectClerkUndo');
    const change = actions.find((a) => a.type === 'updateProcessedEntity');
    const restored = change?.newState ?? {};
    // Name what the undo reversed from the fields it restored: an increment change
    // restores `increment`, a bid restores `bid`, and a floor override restores
    // `badge`/`bidType` only (bid untouched).
    let what = null;
    if ('increment' in restored) what = 'increment';
    else if ('bid' in restored) what = 'bid';
    else if ('badge' in restored || 'bidType' in restored) what = 'floor override';
    return {
      id: entry.processedEntryId,
      kind: 'undo',
      what,
      bid: restored.bid ?? null,
      increment: restored.increment ?? null,
      rejected: !!reject,
      reason: reject?.reason ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'pauseSale') {
    return {
      id: entry.processedEntryId,
      kind: 'pause',
      reason: raw.reason ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'resumeSale') {
    return {
      id: entry.processedEntryId,
      kind: 'resume',
      t,
    };
  }

  if (raw.rawEntryType === 'finalizeSale') {
    // Accepted → updateProcessedEntity (status/bid/badge) + endEngine; rejected
    // when the lot is already finalized → rejectFinalization, with the reason.
    const reject = actions.find((a) => a.type === 'rejectFinalization');
    return {
      id: entry.processedEntryId,
      kind: 'finalize',
      finalState: raw.finalState,
      label: FINAL_STATE_LABEL[raw.finalState] ?? raw.finalState,
      amount: raw.bid,
      badge: raw.badge ?? '',
      rejected: !!reject,
      reason: reject?.rejectReason ?? null,
      t,
    };
  }

  if (raw.rawEntryType === 'proposeBid') {
    const reject = actions.find((a) => a.type === 'rejectBid');
    // `amount` is always on the proposeBid rawEntry; the accepting action's
    // newState.bid is unreliable (the first accept only records status/bidders).
    if (reject) {
      const source = sourceFor(raw, null);
      return {
        id: entry.processedEntryId,
        kind: 'reject',
        amount: raw.amount,
        source,
        bidder: SOURCE_LABEL[source],
        badge: raw.badge ?? '',
        reason: reject.rejectReason ?? 'Rejected',
        t,
      };
    }
    const accept = actions.find((a) => a.type === 'updateProcessedEntity');
    const source = sourceFor(raw, accept);
    return {
      id: entry.processedEntryId,
      kind: 'bid',
      amount: raw.amount,
      source,
      bidder: SOURCE_LABEL[source],
      badge: raw.badge ?? '',
      t,
    };
  }

  return null;
}

/**
 * @param {Array} processedEntries - the item-bidding entity's ledger
 * @returns {Array} history rows (kind: 'bid' | 'reject' | 'increment' | 'asking'), newest first
 */
export function toBidHistory(processedEntries = []) {
  return processedEntries
    .map(toRow)
    .filter(Boolean)
    .reverse();
}
